/**
 * Language setup.
 *
 * Catalogs are TypeScript rather than JSON so they can carry comments — a
 * translator's hardest problem here is not vocabulary but context, and "does
 * `hold` mean the verb or the metric" is answerable in a comment and guesswork in
 * a JSON file. They also give the `t()` function its key types for free.
 *
 * Only the active language is loaded. The two catalogs are over 100 KB of
 * source each, so each is its own chunk, fetched before the first render
 * (`i18nReady`, awaited in `main.tsx`) and on a switch (`changeLanguage` waits
 * for it), so there is never a flash of the wrong language. There is no
 * fallback language: the Spanish catalog is typed against the English one, so
 * a missing key is a compile error rather than a silent fall back.
 */
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { formatNumber, setFormatLocale, shotKeyNumber } from "@scandi/shared";
import { loadCatalog, loadCatalogParts, loadFullCatalog } from "./catalogs";
import type { en } from "./locales/en";
import type { Catalog } from "./locales/en";

/**
 * The languages on offer, and everything that varies with them.
 *
 * `locale` is the tag `Intl` is given: `es-419` is the standard tag for Latin
 * American Spanish, and it is what makes dates read "12 jul" and numbers group
 * as 1,234,567 rather than the European 1.234.567. The calendar, the one
 * component that does not go through `Intl`, takes its date-fns locale from
 * `components/localized-calendar.tsx`.
 *
 * The switcher shows `nativeName`, because a reader looking for their own
 * language is looking for the word they call it by.
 */
export const LANGUAGES = [
  { code: "en", nativeName: "English", locale: "en-GB" },
  { code: "es", nativeName: "Español", locale: "es-419" },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]["code"];

export const defaultNS = "translation";

/** The catalogs' shape, for `i18next.d.ts`; the catalogs themselves load on demand. */
export interface Resources {
  en: { translation: typeof en };
  es: { translation: Catalog };
}

/** Where the choice is remembered. Named so it is greppable. */
const STORAGE_KEY = "scandi-language";

/**
 * The language to open in: a stored choice, else the browser's first supported
 * one, else English. Read here rather than left to the detector so the one
 * catalog needed can be fetched before anything renders; the detector still
 * remembers a later switch.
 */
function initialLanguage(): LanguageCode {
  const supported = (code: string | null | undefined): LanguageCode | null => {
    const base = code?.toLowerCase().split("-")[0];
    return LANGUAGES.find((l) => l.code === base)?.code ?? null;
  };
  try {
    const stored = supported(localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch {
    // Storage blocked: fall through to the browser's languages.
  }
  for (const code of navigator.languages ?? [navigator.language]) {
    const match = supported(code);
    if (match) return match;
  }
  return "en";
}

/**
 * Whether the first catalog has been read. The first paint waits for the core only; the deferred sections
 * follow at once (`catalogPartsReady`). A later switch of language reads core and sections together, so a
 * switched page never shows a key.
 */
let firstRead = true;

/** Adds a language's deferred sections to i18next; components re-render as they land (`bindI18nStore`). */
function addParts(language: LanguageCode): Promise<void> {
  return loadCatalogParts(language).then((parts) => {
    for (const part of parts) i18next.addResourceBundle(language, defaultNS, part, true, true);
  });
}

/** Resolves when the first language's catalog is in; `main.tsx` renders after it. */
export const i18nReady: Promise<unknown> = i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .use({
    type: "backend",
    init() {},
    read(language: string, _namespace: string, callback: (error: unknown, data: Catalog | null) => void) {
      if (!LANGUAGES.some((l) => l.code === language)) return callback(new Error(`No catalog for ${language}`), null);
      // The catalog as `catalogs.ts` prepares it (Spanish gets its exact-millions plural there). The first
      // read is the core alone; a switch reads the language whole.
      const whole = !firstRead;
      firstRead = false;
      (whole ? loadFullCatalog(language as LanguageCode) : loadCatalog(language as LanguageCode)).then(
        (catalog) => callback(null, catalog),
        (error: unknown) => callback(error, null),
      );
    },
  })
  .init({
    lng: initialLanguage(),
    defaultNS,
    ns: [defaultNS],
    fallbackLng: false,
    // `en-GB`, `en-US` and plain `en` are all the English catalog. Without this,
    // a browser set to `es-MX` would miss the `es` catalog entirely.
    load: "languageOnly",
    supportedLngs: LANGUAGES.map((l) => l.code),
    detection: {
      // A stored choice always wins; the browser is only consulted on a first
      // visit. Nothing else is checked, because a query string or a cookie
      // silently overriding a deliberate choice is a bug, not a feature.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: {
      // React escapes for us; doing it twice turns an apostrophe into `&#39;`.
      escapeValue: false,
    },
    // A deferred catalog section landing re-renders whatever reads it.
    react: { bindI18nStore: "added" },
    // A key the catalog does not have renders as the key itself. TypeScript
    // catches the ones written out in full, but not the ones assembled at
    // runtime like `metrics.${key}.label`, so those get said out loud in dev.
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (languages, _ns, key) => {
      console.warn(`i18n missingKey [${languages.join(", ")}] ${key}`);
    },
  });

/**
 * Make `{{count, number}}` in a catalog string mean what the rest of the app
 * means by a number.
 *
 * i18next ships its own `number` formatter, but it builds it from the language
 * tag it was given — `es`, which is European Spanish and writes 1.234.567 with
 * dots. Every other figure on the page comes from the shared formatters at
 * `es-419`, which writes 1,234,567. Two conventions in one sentence is worse
 * than either, so the built-in is replaced with the shared one.
 */
i18next.services.formatter?.add("number", (value) => formatNumber(Number(value)));

/**
 * Spanish names a line, a shot or a voiceover with its article: "la entrega
 * de la Línea 2", "del Hook 1", "en el Plano 3". The names are built at run
 * time ("Línea 2", "Hook 1", "Plano 2 · Línea 3"), so the article cannot sit
 * in the catalog string; `{{line, de}}`, `{{line, a}}`, `{{line, en}}` and
 * `{{line, art}}` add it, by the gender of the name's first word, with the
 * contractions ("del", "al"). A name that already starts with an article
 * ("la presentación", "una opción quitada") or is unknown gets the bare
 * preposition. English strings never use these.
 */
const FEMININE = /^(línea|voz|opción|imagen|toma|presentación|nota|escena|referencia|lectura)$/i;
const MASCULINE = /^(hook|plano|clip|fotograma|audio|guion|concepto|deck|s\d+|l[-\d]\w*)$/i;
function withArticle(value: unknown, preposition: "" | "de" | "a" | "en"): string {
  const name = String(value ?? "");
  const head = name.split(/[\s·]+/)[0] ?? "";
  // "El plano · Plano 2": the article is the name's own; lower it to sit mid-sentence.
  const own = /^(el|la|los|las|un|una|unos|unas|algo)$/i.exec(head);
  if (own) {
    const rest = name.charAt(0).toLowerCase() + name.slice(1);
    if (/^el\s/.test(rest) && (preposition === "de" || preposition === "a")) return `${preposition === "de" ? "del" : "al"} ${rest.slice(3)}`;
    return preposition ? `${preposition} ${rest}` : rest;
  }
  const article = FEMININE.test(head) ? "la" : MASCULINE.test(head) ? "el" : null;
  if (!article) return preposition ? `${preposition} ${name}` : name;
  if (article === "el" && preposition === "de") return `del ${name}`;
  if (article === "el" && preposition === "a") return `al ${name}`;
  return preposition ? `${preposition} ${article} ${name}` : `${article} ${name}`;
}
i18next.services.formatter?.add("art", (value) => withArticle(value, ""));
i18next.services.formatter?.add("de", (value) => withArticle(value, "de"));
i18next.services.formatter?.add("a", (value) => withArticle(value, "a"));
i18next.services.formatter?.add("en", (value) => withArticle(value, "en"));

/**
 * `{{shot, shot}}`: a shot's key as a person calls it - "S03" (or "s03") reads
 * "Shot 3", "Plano 3" in Spanish. The popups are handed the key; this names
 * it. A key that is not `s` and digits is shown as it is.
 */
i18next.services.formatter?.add("shot", (value, lng) => {
  const key = String(value ?? "");
  const n = shotKeyNumber(key);
  return n === null ? key : i18next.t("storyboard.shotCard.name", { n, lng: lng ?? undefined });
});

/**
 * Keep everything that renders a number or a date in step with the language.
 *
 * Dates and money are as much a part of a translation as the words are: a
 * Spanish page that says "hace 5 min" beside "12 Jul" has been half translated.
 * `document.lang` matters too — it is what a screen reader reads the page in,
 * and what the browser offers to translate.
 */
function applyLocale(language: string): void {
  const entry = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];
  setFormatLocale(entry.locale);
  document.documentElement.lang = entry.code;
}

/**
 * The first language's deferred sections, asked for as soon as its core is in. Every lazily loaded page waits
 * for them with its code (`route-pages.ts`), so no page renders before its strings; the shell never uses them.
 * A failure resolves too: those keys show, as a catalog that failed to load always has.
 */
export const catalogPartsReady: Promise<void> = i18nReady
  .then(() => addParts((i18next.resolvedLanguage ?? "en") as LanguageCode))
  .catch(() => undefined);

applyLocale(i18next.resolvedLanguage ?? "en");
i18next.on("languageChanged", applyLocale);
void i18nReady.then(() => applyLocale(i18next.resolvedLanguage ?? "en"));

export default i18next;
